import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Resource } from '../../models/resource';
import { Serializer } from '../../models/serializer';
import { CacheService } from '../cache';
import * as pluralize from 'pluralize';

/**
  * ResourceService
  * 
  * The base class for creating, reading, updating, and deleting 
  * resources via Clooper's REST APIs.
  * This class is to be extended by 
  */

export class ResourceService<T extends Resource> {

  protected serializer: Serializer<T>;
  protected collections: string;
  protected collection: string;

  protected parentId: string;

  /**
  * Creates an instance of the ResourceService for the typed Resource<T>
  *
  * @param {HttpClient} http Injected instance of HttpClient
  * @param {CacheService} cache Injected instance of CacheService
  * @param {string} endpoint Base path to the endpoint of the Resource
  */
  constructor(
    protected http: HttpClient,
    protected cache: CacheService,
    protected endpoint: string
  ) {
    this.serializer = new Serializer<T>();
    this.collections = this.endpoint.toLowerCase();
    this.collection = pluralize.singular(this.endpoint.toLowerCase());
  }

  /**
  * Sends a POST request to the /create endpoint of the typed Resource
  * to create and save a copy of the resource instance on the server
  *
  * @param {Resource} item An instance of a resource to create 
  * @returns {Observable} Returns an observable of typed Resource, with a server-side representation of the created resource
  */
  public create(item: T): Observable<T> {
    return this.http
      .post<T>(`${this.endpoint}`, this.serializer.toJson(item))
      .pipe(map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        return this.serializer.fromJson(resp.data[this.collection]) as T;
      }));
  }

  /**
  * Sends a PUT request to the /update endpoint of the typed Resource
  * to update and save the existing copy of the resource instance on the server
  *
  * @param {Resource} item An instance of a resource to upate 
  * @returns {Observable} Returns an observable of typed Resource, with a server-side representation of the updated resource
  */
  public update(item: T): Observable<T> {
    return this.http
      .put<T>(`${this.endpoint}/${item.id}`, this.serializer.toJson(item))
      .pipe(map((resp: any) => {
        this.cache.remove(`${this.endpoint}/${item.id}`);
        this.cache.remove(`${this.endpoint}`);
        const res = resp.data ? resp.data[this.collection] : (resp[this.collection] || resp);
        return this.serializer.fromJson(res) as T;
      }));
  }

  /**
  * Sends a GET request to the /read endpoint of the typed Resource
  * to read and return a copy of the resource instance on the server.
  * This method returns a copy of the resource from local cache, if it exists.
  *
  * @param {string} id The unique identifier of the resource to read
  * @param {boolean} skipCache Optional boolean flag indicating whether to 
  * read from local cache, or make an http request to the API endpoint. 
  * Forces http request if true, and clears the local cache.
  * @returns {Observable} Returns an observable of typed Resource, with a server-side representation of the resource
  */
  public get(id: string, skipCache?: boolean,  queryOptions?: HttpParams,): Observable<T> {
    if (skipCache === true) {
      this.cache.remove(`${this.endpoint}/${id}`);
    }
    return this.http
      .get<T>(`${this.endpoint}/${id}`, {params: queryOptions})
      .pipe(map((resp: any) => this.serializer.fromJson(resp.data[this.collection]) as T));
  }

  /**
  * Sends a GET request to the /fetch endpoint of the typed Resource
  * to return copies of the resources on the server
  *
  * @param {HttpParams} queryOptions Object to filter the resources by on the server-side 
  * @returns {Observable} Returns an observable of array of typed Resource, with a server-side representations of the matching resources
  */
  public fetch(queryOptions?: HttpParams, raw?: boolean): Observable<T[]> {
    return this.http
      .get<T[]>(`${this.endpoint}`, { params: queryOptions })
      .pipe(map((resp: any) => {
        resp.data[this.collections] = this.convertData(resp.data[this.collections]);
        if (!raw) {
          return resp.data[this.collections];
        }
        return resp;
      }));
  }

  /**
  * Sends a DELETE request to the /delete endpoint of the typed Resource
  * to remove the copy of the resource instance on the server
  *
  * @param {string} id A unique identifier of the resource to delete 
  * @returns {Observable} Returns an observable of type Object, with an empty object
  */
  public delete(id: string) {
    return this.http
      .delete(`${this.endpoint}/${id}`)
      .pipe(map((resp: any) => {
        this.cache.remove(`${this.endpoint}/${id}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      }));
  }

  /**
  * Sends a DELETE request to the /delete endpoint of the typed Resource
  * to remove the copies of the resource instances on the server
  *
  * @param {string[]} ids Unique identifiers for the resources to delete 
  * @returns {Observable} Returns an observable of type Object, with an empty object
  */
   public bulk_delete(ids: string[]): Observable<any> {
    return this.http
      .delete(`${this.endpoint}`, { params: { ids: ids.join(',') } })
      .pipe(map((resp: any) => {
        ids.forEach(id => this.cache.remove(`${this.endpoint}/${id}`));
        this.cache.remove(`${this.endpoint}`);
        return resp;
      }));
  }

  /**
  * Sends a POST request to the /create endpoint of the typed Resource
  * to create and save a copy of the resource instance on the server
  *
  * @param {Resource} item An instance of a resource to create 
  * @returns {Observable} Returns an observable of typed Resource, with a server-side representation of the created resource
  */
  protected convertData(data: any[]): T[] {
    if (data?.length) {
      return data.map((item: any) => this.serializer.fromJson(item) as T);
    } else return [];
  }

  /**
  * Sends a GET request to the /stats endpoint of the typed Resource
  * to read and return some stats about the resource instances on the server
  *
  * @returns {Observable} Returns an observable of typed Resource, with a server-side representation of the created resource
  */
  public stats(): Observable<any> {
    return this.http.get<any>(`${this.endpoint}/stats`)
      .pipe(map((resp: any) => resp.data.stats));
  }

  /**
  * Sends a GET request to the /search endpoint of the typed Resource
  * to find and return copies of matching resource instances on the server
  *
  * @param {HttpParams} queryOptions Object representing fields or parameters to filter/query by
  * @returns {Observable} Returns an observable of array of typed Resources, with a server-side representations of the matching resources
  */
  public search(queryOptions?: HttpParams): Observable<T[]> {
    return this.http.get<T[]>(`${this.endpoint}/search`, { params: queryOptions })
      .pipe(map((resp: any) => {
        resp.data[this.collections] = this.convertData(resp.data[this.collections]);
        return resp
      }));
  }

  /**
  * Sets a parentId on the ResourceService instance, to effectively mutate the endpoint
  * and act as a sub/child resource instance
  * 
  * @param {string} parentId A unique identifier for the parent resource
  * @returns {this} Returns an instance of the class so the method can be chained.
  */
  public setParent(parentId: string) {
    this.parentId = parentId;
    return this;
  }

}
